import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import useAtsJobPostingsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

// State machine — see backend AtsJobPostingDataAccess.ChangeStatus:
//   draft → published (publish)
//   published → paused (pause) | closed (close)
//   paused → published (resume) | closed (close)
//   closed → archived (archive)
//
// `expired` TÜRETİLMİŞ bir durumdur (BP-16): depoda satır hâlâ `published`, bu
// yüzden geçiş kümesi de `published` ile AYNIdır — süresi geçmiş ilan yine
// duraklatılıp kapatılabilir. Süreyi uzatmak için satır DÜZENLENEBİLİR olmalı;
// aksi halde süresi dolan ilan tamir edilemez bir çıkmaza düşerdi.
const PUBLISHED_TRANSITIONS = [
  { action: 'pause', labelKey: 'ats.jobPosting.action.pause' },
  { action: 'close', labelKey: 'ats.jobPosting.action.close' },
];
const ALLOWED_TRANSITIONS = {
  draft: [{ action: 'publish', labelKey: 'ats.jobPosting.action.publish' }],
  published: PUBLISHED_TRANSITIONS,
  expired: PUBLISHED_TRANSITIONS,
  paused: [
    { action: 'resume', labelKey: 'ats.jobPosting.action.resume' },
    { action: 'close', labelKey: 'ats.jobPosting.action.close' },
  ],
  closed: [{ action: 'archive', labelKey: 'ats.jobPosting.action.archive' }],
  archived: [],
};

// Düzenlenebilir durumlar — arka uç yalnız closed/archived satırı dondurur.
const EDITABLE_STATUSES = ['draft', 'published', 'expired', 'paused'];

export default function AtsJobPostingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;
  const location = useLocation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [statusDialog, setStatusDialog] = useState({ isOpen: false, id: null, title: '', action: '', actionLabel: '' });
  const [statusBusy, setStatusBusy] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, title: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    fetching, data, loadFailed, pagination, columns: baseColumns, objectSchema,
    create, update, changeStatus, remove, fetchRequisitionById,
  } = useAtsJobPostingsApi(queryParams);

  // "İlana Çevir" deep-link from the requisition list. URL carries
  // ?createFromRequisition=<requisitionId>; we open the drawer pre-filled with
  // the linked requisition + sensible defaults derived from it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reqId = params.get('createFromRequisition');
    if (!reqId || drawerOpen) return;

    fetchRequisitionById(reqId).then((req) => {
      const initial = { requisitionId: reqId };
      if (req) {
        // Default the posting title from the position's canonical Title.Name
        // (the canonical role label) when present; otherwise leave blank.
        if (req.positionTitleName) initial.title = req.positionTitleName;
      }
      setSelectedRow(initial);
      setDrawerOpen(true);
      // Strip the query param so a refresh doesn't keep re-opening the drawer.
      history.replace({ pathname: location.pathname });
    });
  }, [location.search, location.pathname, drawerOpen, fetchRequisitionById, history]);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    // Channels JSON → array for the multi-select widget.
    let channels = [];
    try { channels = JSON.parse(row.channelsJson || '[]'); } catch { /* keep empty */ }
    setSelectedRow({
      ...row,
      channels,
      expiresAt: row.expiresAt ? row.expiresAt.substring(0, 10) : '',
    });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const ok = selectedRow?.id
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (ok) setDrawerOpen(false);
    return ok;
  }, [selectedRow, create, update]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleStatusConfirm = useCallback(async () => {
    if (!statusDialog.id) return;
    setStatusBusy(true);
    await changeStatus(statusDialog.id, statusDialog.action);
    setStatusDialog({ isOpen: false, id: null, title: '', action: '', actionLabel: '' });
    setStatusBusy(false);
  }, [statusDialog, changeStatus]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    await remove(deleteDialog.id);
    setDeleteDialog({ isOpen: false, id: null, title: '' });
    setDeleting(false);
  }, [deleteDialog, remove]);

  const copyPostingUrl = useCallback(async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('ats.jobPosting.action.urlCopied'));
    } catch {
      toast.error(t('common.error'));
    }
  }, [t]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    // Available to everyone who can see the posting (incl. an AT.Read Hiring
    // Manager who owns the requisition): drill into this posting's applicants.
    const actions = [{
      type: 'view_applications',
      label: t('ats.jobPosting.action.viewApplications'),
      onClick: () => history.push({
        pathname: '/ats/applications',
        search: `?jobPostingId=${row.id}`,
        // Başvurular sayfasının kapsam afişi başlığı buradan alır (yoksa GET ile çözer).
        state: { postingFilter: { id: row.id, title: row.title } },
      }),
    }];
    // FE-15 — bu ilanın kanban panosu. Yalnız izlemek AT.Read'e açıktır (sürükle-bırak
    // taşıma panoda ayrıca yazma yetkisi ister), bu yüzden aksiyon writable kapısının
    // ÜSTÜNDE durur — HM kendi ilanının hunisini görebilmeli.
    actions.push({
      type: 'pipeline_board',
      label: t('ats.jobPosting.action.pipelineBoard'),
      onClick: () => history.push({
        pathname: `/ats/job-postings/${row.id}/pipeline`,
        state: { postingTitle: row.title },
      }),
    });
    if (!writable) return actions;

    // FE-10 — bu ilanın süreç aşamaları. İlana özel liste YOKSA ekran önce
    // "İlana Özel Liste Oluştur" (kiracı varsayılanlarının kopyası) sunar;
    // tek satırlık override ilanın bütün başvurularını hattın dışına düşürürdü.
    actions.push({
      type: 'pipeline_stages',
      label: t('ats.jobPosting.action.pipelineStages'),
      onClick: () => history.push({
        pathname: '/ats/settings',
        search: `?tab=stages&jobPostingId=${row.id}`,
        state: { postingTitle: row.title },
      }),
    });

    // Editable while the posting is live or pre-publish — a typo in a
    // published ad must be fixable without closing it. Closed/archived rows
    // stay frozen (backend enforces the same rule).
    if (EDITABLE_STATUSES.includes(row.status)) {
      actions.push({
        type: 'edit',
        label: t('ats.jobPosting.action.edit'),
        onClick: () => handleEdit(row),
      });
    }

    // Public URL — only meaningful once the posting has been published and
    // PostingUrl is filled (auto on first publish, can also be a recruiter override).
    if (row.postingUrl) {
      actions.push({
        type: 'open',
        label: t('ats.jobPosting.action.openPublic'),
        onClick: () => window.open(row.postingUrl, '_blank', 'noopener,noreferrer'),
      });
      actions.push({
        type: 'copy',
        label: t('ats.jobPosting.action.copyUrl'),
        onClick: () => copyPostingUrl(row.postingUrl),
      });
    }

    const transitions = ALLOWED_TRANSITIONS[row.status] || [];
    transitions.forEach((tr) => {
      actions.push({
        type: tr.action,
        label: t(tr.labelKey),
        onClick: () => setStatusDialog({
          isOpen: true,
          id: row.id,
          title: row.title,
          action: tr.action,
          actionLabel: t(tr.labelKey),
        }),
      });
    });
    if (actions.length > 0) actions.push({ type: 'divider' });
    actions.push({
      type: 'delete',
      label: t('common.delete'),
      onClick: () => setDeleteDialog({ isOpen: true, id: row.id, title: row.title }),
    });
    return actions;
  }, [t, writable, handleEdit, copyPostingUrl, history]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow?.id ? t('ats.jobPosting.edit') : t('ats.jobPosting.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={statusDialog.isOpen}
        title={statusDialog.actionLabel}
        message={
          t('ats.jobPosting.statusChangeConfirm', { title: statusDialog.title, action: statusDialog.actionLabel })
          + (statusDialog.action === 'close' ? ` ${t('ats.jobPosting.closeInfo')}` : '')
        }
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusDialog({ isOpen: false, id: null, title: '', action: '', actionLabel: '' })}
        loading={statusBusy}
        type="primary"
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.jobPosting.deleteConfirm', { title: deleteDialog.title })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, title: '' })}
        loading={deleting}
      />

      {loadFailed && <LoadErrorBanner message={t('ats.jobPosting.loadFailed')} />}

      {useMemo(() => (
        <DataTable
          title={t('ats.jobPosting.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
